import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { StatusFilter, type TemplateListStatus } from '../automations-tab/templates-manager/types';
import { validateTenantSegments } from '../automations-tab/template-api';
import {
  toTemplateTenantSelection,
  toTenantTargetingPayload,
  type TenantSegmentConflict,
} from '../automations-tab/templates.types';
import { useSyncAutomationTemplates, useUpdateAutomationTemplate } from '../useAutomationTemplates';
import { useAutomationEditorContext } from './AutomationEditorContext';
import { getAutomationTemplateSaveButtonLabel } from './automationTemplateSaveButtonLabel';
import { getTemplateSegmentUsage } from './segment-picker/segment-usage';
import { useAutomationTemplateTenantSelection } from './useAutomationTemplateTenantSelection';
import { useSyncStatusHandshakeLock } from './useSyncStatusHandshakeLock';

/**
 * Orchestrates everything EditAutomationTemplateContent renders: the tenant-selection data, the
 * derived template status/availability copy, the sync/disabled state, and the save handlers. Keeps
 * the component presentational and lets the toolbar controls read `isSyncing` from context rather
 * than receiving it as a drilled prop.
 */
export function useEditAutomationTemplateContent() {
  const { editor } = useAutomationEditorContext();
  const { state, markClean, template, undoReorder } = editor;
  const {
    tenantSelection,
    localContents,
    hasReorderedOrAdded,
    hasStepsNeedingSetup,
    hasDuplicateSegments,
    isDirty,
    dirtySteps,
  } = state;

  const updateMutation = useUpdateAutomationTemplate();
  const syncMutation = useSyncAutomationTemplates();
  const [showReorderWarning, setShowReorderWarning] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [segmentAdditions, setSegmentAdditions] = useState<TenantSegmentConflict[] | null>(null);
  const [isCheckingSegmentAdditions, setIsCheckingSegmentAdditions] = useState(false);
  const { addToast } = useToast();

  const {
    isValidating,
    tenantRowData,
    syncingTenantCount,
    jobSyncActive,
    isLoading: isLoadingTenants,
  } = useAutomationTemplateTenantSelection({
    templateId: template.id,
    tenantSelection,
  });

  const syncStatusHandshakeLock = useSyncStatusHandshakeLock(jobSyncActive, {
    isPending: syncMutation.isPending,
    isError: syncMutation.isError,
    isSuccess: syncMutation.isSuccess,
  });

  const isSyncing =
    jobSyncActive || syncMutation.isPending || updateMutation.isPending || syncStatusHandshakeLock;

  const totalTenantCount = Object.keys(tenantRowData).length;
  const isDraft =
    tenantSelection.selectionType === 'selected' && tenantSelection.selectedTenantIds.length === 0;
  const anyTenantInUse = Object.values(tenantRowData).some(row => row.isActive);

  let templateStatus: TemplateListStatus = StatusFilter.Available;
  if (isDraft) {
    templateStatus = StatusFilter.Draft;
  } else if (anyTenantInUse) {
    templateStatus = StatusFilter.Active;
  }

  const excludedTenantCount = tenantSelection.excludedTenantIds.length;
  let availabilitySummary: string;
  if (isDraft) {
    availabilitySummary = 'Not synced to any tenant yet';
  } else if (tenantSelection.selectionType === 'all' && excludedTenantCount > 0) {
    availabilitySummary = `Available to all tenants except ${excludedTenantCount} (and new ones)`;
  } else if (tenantSelection.selectionType === 'all') {
    availabilitySummary = `Available to all ${totalTenantCount} tenants (and new ones)`;
  } else {
    availabilitySummary = `Available to ${tenantSelection.selectedTenantIds.length} of ${totalTenantCount} tenants`;
  }

  const handleSave = () => {
    const tenantTargetingAtSave = toTenantTargetingPayload(tenantSelection);
    const loadedTenantTargeting = toTenantTargetingPayload(toTemplateTenantSelection(template.tenants));
    const contentsAreDirty =
      dirtySteps.trigger || dirtySteps.settings || dirtySteps.actions.size > 0 || hasReorderedOrAdded;

    updateMutation.mutate(
      {
        id: template.id,
        params: {
          // A tenant-only save must not write the contents snapshot. Generation can replace
          // those contents while this editor is open, and posting them would wipe that copy.
          ...(contentsAreDirty ? { contents: localContents } : {}),
          ...tenantTargetingAtSave,
          previousTargetedTenantsFilter: loadedTenantTargeting.targetedTenantsFilter,
          previousExcludedTenantIds: loadedTenantTargeting.excludedTenantIds ?? [],
        },
      },
      {
        onSuccess: () => {
          markClean();
          syncMutation.mutate({ templateId: template.id, silent: true });
        },
      },
    );
  };

  const saveAfterConfirmingSegmentAdditions = async () => {
    const isDraftSelection =
      tenantSelection.selectionType === 'selected' && tenantSelection.selectedTenantIds.length === 0;
    if (isDraftSelection) {
      handleSave();
      return;
    }
    setIsCheckingSegmentAdditions(true);
    try {
      const segmentDefinitionIds = [...getTemplateSegmentUsage(localContents).keys()];
      const { conflicts } = await validateTenantSegments(
        template.id,
        tenantSelection,
        segmentDefinitionIds,
      );
      if (conflicts.length > 0) {
        setSegmentAdditions(conflicts);
        return;
      }
      handleSave();
    } catch {
      addToast({
        title: 'Unable to save',
        description: "Couldn't check which tenants will receive new segments. Try saving again.",
        variant: 'destructive',
      });
    } finally {
      setIsCheckingSegmentAdditions(false);
    }
  };

  const handleSaveClick = hasReorderedOrAdded
    ? () => setShowReorderWarning(true)
    : saveAfterConfirmingSegmentAdditions;

  const saveDisabled =
    !isDirty ||
    hasStepsNeedingSetup ||
    hasDuplicateSegments ||
    isValidating ||
    isCheckingSegmentAdditions ||
    updateMutation.isPending ||
    syncMutation.isPending ||
    isSyncing;

  let saveTitle: string | undefined;
  if (hasStepsNeedingSetup) {
    saveTitle = 'Please configure all steps before saving';
  } else if (hasDuplicateSegments) {
    saveTitle = 'A segment is both the audience and an exclusion. Change one before saving.';
  } else if (isValidating) {
    saveTitle = 'Validating tenant segment compatibility…';
  } else if (isSyncing) {
    saveTitle = 'Sync in progress';
  } else if (saveDisabled && !isDirty) {
    saveTitle = 'No changes to save';
  }

  const saveButtonLabel = getAutomationTemplateSaveButtonLabel({
    isUpdatePending: updateMutation.isPending,
    jobSyncActive,
    syncingTenantCount,
    isSyncMutationPending: syncMutation.isPending,
  });

  return {
    name: localContents.name,
    description: localContents.description,
    templateStatus,
    availabilitySummary,
    isDraft,
    isSyncing,
    templateId: template.id,
    tenantRowData,
    isValidating,
    isLoadingTenants,
    hasReorderedOrAdded,
    undoReorder,
    tenantSelection,
    saveButtonLabel,
    saveDisabled,
    saveTitle,
    handleSaveClick,
    saveAfterConfirmingSegmentAdditions,
    segmentAdditions,
    closeSegmentAdditions: () => setSegmentAdditions(null),
    handleSave,
    showReorderWarning,
    setShowReorderWarning,
    tenantDialogOpen,
    openTenantDialog: () => setTenantDialogOpen(true),
    closeTenantDialog: () => setTenantDialogOpen(false),
  };
}
